<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-bottom">
            <div class="head-card-top mb-2">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-dsp' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-dsp', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Overview') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-dsp-partners' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-dsp-partners', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('DSP Partners') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-geo' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-geo', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Geo') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-device' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-device', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Device') }}
              </b-button>
            </div>
          </div>
          <div class="head-card-top">
            <h4>{{ $t('dsppartners.navigation.devices') }}: <em>{{ deviceOsValue }}</em></h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @input="toggleRange"
              @refresh="fetchDeviceOsDetails"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >
        <template #head(date)>
          {{ $t('stats.date') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(clicks)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.spent') }}
        </template>
        <template #head(tax)>
          {{ $t('stats.tax') }}
        </template>
        <template #cell(date)="{ item }">
          {{ getFormattedDate(new Date(item.date)) }}
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(clicks)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ data.item.revenue && data.item.tax ? currency('USD', data.item.revenue - data.item.tax) : (data.value ? currency('USD', data.value) : 0) }}
        </template>
        <template #cell(tax)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>
        <template #foot(date)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(clicks)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('clicks')) }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sumRevenueMinusTax()) }}
        </template>
        <template #foot(tax)>
          {{ currency('USD', sum('tax')) }}
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import PartnerToolbar from '@/views/dashboard/partners/PartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'DeviceOsDetails',
  components: {
    BCard,
    BTable,
    BSpinner,
    Breadcrumbs,
    StatsNavigation,
    StatsDateRangeFilter,
    PartnerToolbar,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'date', label: 'Дата', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'tax', label: 'Tax', sortable: true },
      ],
      breadcrumbItems: [],
      partner: {},
      deviceOsValue: '',
      deviceOsType: 'device', // По умолчанию device
      isLoading: false,
    }
  },
  computed: {
    selectedRangeFormated() {
      if (!this.rangePicker || !Array.isArray(this.rangePicker) || this.rangePicker.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      return {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
    },
  },
  created() {
    this.getPartnerData()

    // Определяем тип и значение из роута
    if (this.$route.params.deviceId) {
      this.deviceOsType = 'device'
      this.deviceOsValue = this.$route.params.deviceId
    } else if (this.$route.params.osId) {
      this.deviceOsType = 'os'
      this.deviceOsValue = this.$route.params.osId
    } else {
      this.deviceOsType = 'device' // По умолчанию device
    }

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.fetchDeviceOsDetails() // Вызываем только один раз при инициализации
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.fetchDeviceOsDetails() // Вызываем только один раз при инициализации
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getPartner(this.$route.params.id)
      this.partner = responseData.data.partner || {}
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      const deviceOsStatsPath = this.deviceOsType === 'device'
        ? `/partners/${this.partner._id}/stats/device`
        : `/partners/${this.partner._id}/stats/os`

      this.breadcrumbItems = [
        {
          text: this.$t('Partners'),
          to: '/partners',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}`,
        },
        {
          text: this.$t('Statistics'),
          to: `/partners/${this.partner._id}/stats`,
        },
        {
          text: this.$t('dsppartners.navigation.devices'),
          to: deviceOsStatsPath,
        },
        {
          text: this.deviceOsValue,
          active: true,
        },
      ]
    },
    async fetchDeviceOsDetails() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated

      try {
        let response
        if (this.deviceOsType === 'device') {
          response = await useJwt.getDsppartnerStatsDevice(
            null,
            {
              from,
              to,
              type: 'daily',
              device: this.deviceOsValue,
              openrtb_partner_id: this.$route.params.id,
            },
          )
        } else {
          response = await useJwt.getDsppartnerStatsOs(
            null,
            {
              from,
              to,
              type: 'daily',
              os: this.deviceOsValue,
              openrtb_partner_id: this.$route.params.id,
            },
          )
        }
        this.rows = (response.data.items || []).map(item => ({
          ...item,
        }))
      } catch (error) {
        this.rows = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    sumRevenueMinusTax() {
      return this.rows.reduce((acc, r) => acc + ((r.revenue || 0) - (r.tax || 0)), 0)
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Обновляем URL без перезагрузки страницы
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.currentPage = 1 // Reset to first page when changing date range
        this.fetchDeviceOsDetails()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
